import { useState } from 'react';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const xrTypes = [
  {
    id: 'vr',
    name: 'VR 虚拟现实',
    icon: 'ri-glasses-2-line',
    tagColor: '#00FF88',
    splatColor: 'rgba(0,255,136,0.18)',
    desc: '完全沉浸式虚拟环境，用户置身于全数字化构建的三维世界，实现与现实完全隔离的深度沉浸体验。',
    features: ['360°全景视觉', '空间音频系统', '手势追踪交互', '触觉反馈手套'],
    story: '2030年，一位建筑师在VR中完成了整栋大楼的设计——她在虚拟空间里走进每一个房间，感受光线，调整比例，最终交付的方案零返工。客户戴上VR头显，在建筑建成前就已经"住进去"体验了三个月。',
    img: 'https://public.readdy.ai/ai/img_res/400358bc321ebe67c35c11def9fb7869.jpg',
    spec: '8K分辨率 · 120Hz · FOV 220°',
  },
  {
    id: 'ar',
    name: 'AR 增强现实',
    icon: 'ri-focus-3-line',
    tagColor: '#FFD700',
    splatColor: 'rgba(255,215,0,0.18)',
    desc: '将数字信息叠加于真实世界之上，通过智能眼镜或手机屏幕实现虚实融合的增强感知体验。',
    features: ['实时环境识别', '数字信息叠加', '空间锚点定位', '多人协作共享'],
    story: '2028年，外科医生戴着AR眼镜做手术，实时的3D器官模型叠加在患者身上，手术精度提升了40%，手术时间缩短了一半。同年，建筑工人用AR眼镜看到了墙壁内部的管道走向，施工错误率降至零。',
    img: 'https://readdy.ai/api/search-image?query=AR%20augmented%20reality%20holographic%20overlay%20digital%20information%20floating%20real%20world%20golden%20yellow%20neon%20glow%20futuristic%20technology%20dark%20background%20doodle%20art%20style%20vibrant%20colorful%20ultra%20detailed%208k&width=800&height=500&seq=xr-ar-doodle-v2-001&orientation=landscape',
    spec: '全天候佩戴 · 12h续航 · 毫米级定位',
  },
  {
    id: 'mr',
    name: 'MR 混合现实',
    icon: 'ri-merge-cells-horizontal',
    tagColor: '#FF6B35',
    splatColor: 'rgba(255,107,53,0.18)',
    desc: '虚拟对象与真实环境深度融合，数字内容能够感知并响应物理世界，实现真正的虚实交互体验。',
    features: ['物理感知融合', '虚实遮挡处理', '实时光照匹配', '多模态交互'],
    story: '2029年，一个工程师团队在MR环境中协作维修一台从未见过的设备——虚拟操作手册漂浮在零件旁边，AI实时指导每一步操作。远在地球另一端的专家的双手以全息形态出现在现场，直接"触摸"设备进行指导。',
    img: 'https://readdy.ai/api/search-image?query=mixed%20reality%20MR%20holographic%20objects%20blending%20real%20environment%20orange%20neon%20glow%20futuristic%20technology%20dark%20background%20doodle%20art%20style%20vibrant%20colorful%20ultra%20detailed%208k%20cinematic&width=800&height=500&seq=xr-mr-doodle-v2-001&orientation=landscape',
    spec: '物理感知精度1mm · 实时光照匹配',
  },
  {
    id: 'hologram',
    name: '全息投影',
    icon: 'ri-projector-line',
    tagColor: '#A855F7',
    splatColor: 'rgba(168,85,247,0.18)',
    desc: '裸眼3D全息显示技术，无需任何穿戴设备即可呈现立体数字内容，开创人机交互的全新维度。',
    features: ['裸眼3D显示', '空中触控交互', '多角度观看', '实时渲染投影'],
    story: '2031年，一场演唱会的全息歌手在舞台上与真人乐队同台演出，观众无法分辨哪个是真实的。同年，已故科学家的全息影像在诺贝尔颁奖典礼上发表了感言，全场泪目。',
    img: 'https://readdy.ai/api/search-image?query=hologram%20projection%203D%20holographic%20display%20floating%20digital%20content%20purple%20neon%20glow%20dark%20background%20futuristic%20technology%20doodle%20art%20style%20vibrant%20colorful%20ultra%20detailed%208k%20cinematic&width=800&height=500&seq=xr-holo-doodle-v2-001&orientation=landscape',
    spec: '裸眼3D · 空中触控 · 360°观看',
  },
];

const devices = [
  { name: 'META Vision Pro', type: 'VR头显', spec: '8K · 120Hz', color: '#00FF88', img: 'https://readdy.ai/api/search-image?query=futuristic%20VR%20headset%20sleek%20design%20glowing%20green%20neon%20dark%20background%20product%20shot%20technology%20doodle%20art%20style%20ultra%20detailed%20artistic&width=300&height=200&seq=xr-device-vr-doodle-001&orientation=landscape' },
  { name: 'META Lens X', type: 'AR眼镜', spec: '全天候 · 12h', color: '#FFD700', img: 'https://readdy.ai/api/search-image?query=futuristic%20AR%20smart%20glasses%20sleek%20design%20glowing%20yellow%20neon%20dark%20background%20product%20shot%20technology%20doodle%20art%20style%20ultra%20detailed%20artistic&width=300&height=200&seq=xr-device-ar-doodle-001&orientation=landscape' },
  { name: 'META Glove', type: '触觉手套', spec: '256点触觉', color: '#FF6B35', img: 'https://readdy.ai/api/search-image?query=haptic%20feedback%20glove%20futuristic%20technology%20glowing%20orange%20neon%20dark%20background%20product%20shot%20sci-fi%20doodle%20art%20style%20ultra%20detailed%20artistic&width=300&height=200&seq=xr-device-glove-doodle-001&orientation=landscape' },
  { name: 'META Suit', type: '全身追踪', spec: '全身动捕', color: '#A855F7', img: 'https://readdy.ai/api/search-image?query=full%20body%20motion%20capture%20suit%20futuristic%20technology%20glowing%20purple%20neon%20dark%20background%20product%20shot%20sci-fi%20doodle%20art%20style%20ultra%20detailed%20artistic&width=300&height=200&seq=xr-device-suit-doodle-001&orientation=landscape' },
];

// Mr. Doodle SVG decorations for XR section
function XRDoodleDecorations({ activeColor }: { activeColor: string }) {
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox="0 0 1440 900"
      preserveAspectRatio="none"
      style={{ opacity: 0.2 }}
    >
      {/* Top wavy border */}
      <path
        d="M0,45 Q100,15 200,50 Q300,85 400,45 Q500,5 600,48 Q700,91 800,45 Q900,0 1000,48 Q1100,96 1200,48 Q1300,0 1440,45"
        stroke={activeColor} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.5"
      />
      {/* Bottom wavy border */}
      <path
        d="M0,855 Q120,825 240,858 Q360,891 480,852 Q600,813 720,858 Q840,903 960,855 Q1080,807 1200,855 Q1320,903 1440,852"
        stroke="#FFD700" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.4"
      />
      {/* Left decorative squiggle */}
      <path
        d="M25,120 Q55,150 25,180 Q-5,210 25,240 Q55,270 25,300 Q-5,330 25,360 Q55,390 25,420 Q-5,450 25,480"
        stroke="#FF6B9D" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.4"
      />
      {/* Right decorative squiggle */}
      <path
        d="M1415,200 Q1385,230 1415,260 Q1445,290 1415,320 Q1385,350 1415,380 Q1445,410 1415,440 Q1385,470 1415,500 Q1445,530 1415,560"
        stroke="#00FFCC" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.4"
      />
      {/* Large doodle circles */}
      <circle cx="100" cy="200" r="80" stroke={activeColor} strokeWidth="2.5" fill="none" strokeDasharray="10,8" opacity="0.3"/>
      <circle cx="100" cy="200" r="55" stroke={activeColor} strokeWidth="1.5" fill="none" strokeDasharray="5,10" opacity="0.18"/>
      <circle cx="1340" cy="700" r="100" stroke="#A855F7" strokeWidth="2.5" fill="none" strokeDasharray="12,9" opacity="0.28"/>
      <circle cx="1340" cy="700" r="70" stroke="#A855F7" strokeWidth="1.5" fill="none" strokeDasharray="6,11" opacity="0.16"/>
      {/* Mid decorative circle */}
      <circle cx="720" cy="450" r="180" stroke="rgba(255,255,255,0.06)" strokeWidth="1" fill="none" strokeDasharray="8,12"/>
      {/* Star shapes */}
      <path d="M300,50 L306,32 L312,50 L330,50 L316,62 L322,80 L306,68 L290,80 L296,62 L282,50 Z" stroke="#FFD700" strokeWidth="1.5" fill="none" opacity="0.5"/>
      <path d="M1140,60 L1145,45 L1150,60 L1165,60 L1153,70 L1158,85 L1145,75 L1132,85 L1137,70 L1125,60 Z" stroke={activeColor} strokeWidth="1.5" fill="none" opacity="0.45"/>
      <path d="M60,750 L65,735 L70,750 L85,750 L73,760 L78,775 L65,765 L52,775 L57,760 L45,750 Z" stroke="#00FFCC" strokeWidth="1.5" fill="none" opacity="0.4"/>
      <path d="M1380,150 L1384,138 L1388,150 L1400,150 L1390,158 L1394,170 L1384,162 L1374,170 L1378,158 L1368,150 Z" stroke="#FF6B9D" strokeWidth="1.5" fill="none" opacity="0.4"/>
      {/* Doodle arrows */}
      <path d="M450,870 Q470,850 490,870" stroke="#00FF88" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4"/>
      <path d="M485,862 L490,870 L482,876" stroke="#00FF88" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4"/>
      <path d="M950,30 Q970,10 990,30" stroke="#FF6B35" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4"/>
      <path d="M985,22 L990,30 L982,36" stroke="#FF6B35" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4"/>
      {/* Cross marks */}
      <line x1="720" y1="30" x2="720" y2="55" stroke={activeColor} strokeWidth="2.5" strokeLinecap="round" opacity="0.5"/>
      <line x1="707" y1="42" x2="733" y2="42" stroke={activeColor} strokeWidth="2.5" strokeLinecap="round" opacity="0.5"/>
      <line x1="1400" y1="600" x2="1400" y2="625" stroke="#FFD700" strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
      <line x1="1387" y1="612" x2="1413" y2="612" stroke="#FFD700" strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
      <line x1="40" y1="600" x2="40" y2="625" stroke="#FF6B9D" strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
      <line x1="27" y1="612" x2="53" y2="612" stroke="#FF6B9D" strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
      {/* Dot clusters */}
      <circle cx="600" cy="30" r="5" fill="#00FFCC" opacity="0.5"/>
      <circle cx="618" cy="24" r="3.5" fill="#00FFCC" opacity="0.4"/>
      <circle cx="636" cy="32" r="6" fill="#00FFCC" opacity="0.35"/>
      <circle cx="840" cy="870" r="5" fill="#FF6B35" opacity="0.5"/>
      <circle cx="858" cy="864" r="3.5" fill="#FF6B35" opacity="0.4"/>
      <circle cx="876" cy="872" r="6" fill="#FF6B35" opacity="0.35"/>
      {/* Doodle text */}
      <text x="40" y="500" fontSize="20" fill={activeColor} opacity="0.1" fontFamily="Caveat, cursive" transform="rotate(-90, 40, 500)">XR EXPERIENCE</text>
      <text x="1400" y="400" fontSize="18" fill="#A855F7" opacity="0.1" fontFamily="Caveat, cursive" transform="rotate(90, 1400, 400)">IMMERSIVE</text>
      {/* Zigzag bottom */}
      <path d="M500,880 L520,860 L540,880 L560,860 L580,880 L600,860 L620,880 L640,860 L660,880 L680,860 L700,880" stroke="#A855F7" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.3"/>
      {/* Spiral */}
      <path d="M200,800 Q220,780 240,800 Q260,820 240,840 Q220,860 200,840 Q180,820 200,800" stroke="#FFD700" strokeWidth="1.5" fill="none" opacity="0.3"/>
      {/* Heart */}
      <path d="M1200,820 Q1200,808 1210,808 Q1220,808 1220,820 Q1220,808 1230,808 Q1240,808 1240,820 Q1240,835 1220,848 Q1200,835 1200,820 Z" stroke="#FF6B9D" strokeWidth="1.5" fill="none" opacity="0.35"/>
    </svg>
  );
}

export default function XRExperience() {
  const [activeXR, setActiveXR] = useState('vr');
  const [showStory, setShowStory] = useState(false);
  const current = xrTypes.find((x) => x.id === activeXR)!;

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: tabsRef, isVisible: tabsVisible } = useScrollAnimation({ threshold: 0.08 });
  const { ref: mainRef, isVisible: mainVisible } = useScrollAnimation({ threshold: 0.06 });
  const { ref: devicesRef, visibleItems: deviceVisible } = useStaggerAnimation(devices.length, { threshold: 0.08 });

  return (
    <section id="xr" className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(180deg, #060E0A 0%, #070F0B 50%, #060E0A 100%)' }}>
      {/* Sci-fi watermark */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=XR%20virtual%20augmented%20mixed%20reality%20technology%20panorama%20holographic%20displays%20immersive%20experience%20dark%20background%20sci-fi%20blueprint%20ultra%20detailed%208k%20wide&width=1920&height=1080&seq=xr-watermark-bg-001&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-4"
          style={{ transition: 'opacity 0.5s' }}
        />
        <div
          className="absolute inset-0 transition-all duration-700"
          style={{ background: `radial-gradient(ellipse at 40% 50%, ${current.splatColor} 0%, transparent 60%)` }}
        />
        {/* Doodle grid */}
        <div className="absolute inset-0 opacity-12" style={{
          backgroundImage: `linear-gradient(${current.tagColor}15 1px, transparent 1px), linear-gradient(90deg, ${current.tagColor}15 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
          transition: 'background-image 0.5s',
        }} />
      </div>

      {/* Mr. Doodle SVG decorations */}
      <XRDoodleDecorations activeColor={current.tagColor} />

      {/* Ambient glow */}
      <div
        className="absolute top-1/3 left-0 w-[500px] h-[500px] rounded-full pointer-events-none transition-all duration-700"
        style={{ background: `radial-gradient(circle, ${current.splatColor} 0%, transparent 70%)`, filter: 'blur(60px)' }}
      />
      <div className="absolute bottom-1/3 right-0 w-[400px] h-[400px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,255,204,0.05) 0%, transparent 70%)', filter: 'blur(50px)' }} />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="text-center mb-14"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <div
            className="inline-flex items-center gap-2 px-5 py-2 mb-6 text-sm font-bold"
            style={{
              background: `${current.tagColor}12`,
              border: `1.5px solid ${current.tagColor}45`,
              borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
              color: current.tagColor,
              fontFamily: 'Caveat, cursive',
              fontSize: '1rem',
              transition: 'all 0.5s ease',
            }}
          >
            <i className="ri-glasses-2-line" />
            XR Experience Matrix · 沉浸体验
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            XR 体验
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: `linear-gradient(135deg, ${current.tagColor}, #A855F7)`, transition: 'background-image 0.5s' }}
            > 矩阵</span>
          </h2>
          <p className="text-white/40 text-base max-w-2xl mx-auto" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.05rem' }}>
            虚拟现实 · 增强现实 · 混合现实 · 全息投影，四维感官的完全沉浸
          </p>

          {/* Doodle decorative dots row */}
          <div className="flex items-center justify-center gap-3 mt-6">
            {xrTypes.map((x) => (
              <div
                key={x.id}
                className="transition-all duration-300"
                style={{
                  width: activeXR === x.id ? '32px' : '8px',
                  height: '8px',
                  background: x.tagColor,
                  borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                  opacity: activeXR === x.id ? 1 : 0.3,
                  boxShadow: activeXR === x.id ? `0 0 10px ${x.tagColor}` : 'none',
                }}
              />
            ))}
          </div>
        </div>

        {/* XR Type Tabs */}
        <div
          ref={tabsRef as React.RefObject<HTMLDivElement>}
          className="flex flex-wrap gap-3 justify-center mb-10"
          style={{
            opacity: tabsVisible ? 1 : 0,
            transform: tabsVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease 0.15s, transform 0.7s ease 0.15s',
          }}
        >
          {xrTypes.map((x) => (
            <button
              key={x.id}
              onClick={() => { setActiveXR(x.id); setShowStory(false); }}
              className="flex items-center gap-2 px-5 py-2.5 transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{
                borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                border: activeXR === x.id ? `2px solid ${x.tagColor}` : '1.5px solid rgba(255,255,255,0.12)',
                color: activeXR === x.id ? x.tagColor : 'rgba(255,255,255,0.45)',
                backgroundColor: activeXR === x.id ? `${x.tagColor}14` : 'transparent',
                fontFamily: 'Caveat, cursive',
                fontSize: '1rem',
                transform: activeXR === x.id ? 'scale(1.06)' : 'scale(1)',
                boxShadow: activeXR === x.id ? `0 0 20px ${x.tagColor}25` : 'none',
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${x.icon} text-sm`} />
              </div>
              <span className="font-semibold">{x.name}</span>
            </button>
          ))}
        </div>

        {/* XR Detail */}
        <div
          ref={mainRef as React.RefObject<HTMLDivElement>}
          className="flex flex-col lg:flex-row gap-8 mb-16"
          style={{
            opacity: mainVisible ? 1 : 0,
            transform: mainVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
          }}
        >
          {/* Image panel */}
          <div
            className="flex-1 relative rounded-2xl overflow-hidden border transition-all duration-500"
            style={{
              minHeight: '400px',
              borderColor: `${current.tagColor}35`,
              borderRadius: '20px 14px 22px 16px / 14px 22px 16px 20px',
            }}
          >
            <img
              key={current.id}
              src={current.img}
              alt={current.name}
              className="w-full h-full object-cover object-top"
              style={{ transition: 'opacity 0.5s ease' }}
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(6,14,10,0.75) 0%, transparent 50%)' }} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(6,14,10,0.7) 0%, transparent 50%)' }} />

            {/* Mr. Doodle overlay on image */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 800 500" preserveAspectRatio="none" style={{ opacity: 0.25 }}>
              <path d="M0,30 Q100,10 200,35 Q300,60 400,28 Q500,0 600,32 Q700,64 800,28" stroke={current.tagColor} strokeWidth="2" fill="none" strokeDasharray="8,12"/>
              <circle cx="50" cy="420" r="35" stroke={current.tagColor} strokeWidth="1.5" fill="none" strokeDasharray="5,8"/>
              <circle cx="750" cy="80" r="28" stroke={current.tagColor} strokeWidth="1.5" fill="none" strokeDasharray="4,7"/>
              <path d="M680,450 L690,435 L700,450 L715,450 L703,460 L708,475 L690,465 L672,475 L677,460 L665,450 Z" stroke={current.tagColor} strokeWidth="1.5" fill="none" opacity="0.7"/>
              <path d="M30,100 Q50,80 70,100" stroke={current.tagColor} strokeWidth="2" fill="none" strokeLinecap="round"/>
              <path d="M65,92 L70,100 L62,106" stroke={current.tagColor} strokeWidth="2" fill="none" strokeLinecap="round"/>
              <text x="20" y="480" fontSize="16" fill={current.tagColor} opacity="0.4" fontFamily="Caveat, cursive">{current.spec}</text>
            </svg>

            {/* Spec badge */}
            <div className="absolute top-4 left-4">
              <span
                className="px-3 py-1.5 text-xs font-bold"
                style={{
                  borderColor: `${current.tagColor}55`,
                  color: current.tagColor,
                  backgroundColor: `${current.tagColor}18`,
                  border: `1.5px solid ${current.tagColor}55`,
                  fontFamily: 'Caveat, cursive',
                  fontSize: '0.88rem',
                  borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                }}
              >
                {current.spec}
              </span>
            </div>

            {/* Story teaser */}
            <div className="absolute bottom-4 left-4 right-4">
              <button
                onClick={() => setShowStory(!showStory)}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-white/15 bg-black/65 backdrop-blur-sm hover:border-white/30 transition-all duration-300 cursor-pointer text-left"
              >
                <div
                  className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0"
                  style={{ backgroundColor: `${current.tagColor}22`, border: `1px solid ${current.tagColor}40` }}
                >
                  <i className="ri-book-open-line text-xs" style={{ color: current.tagColor }} />
                </div>
                <p className="text-white/70 text-xs flex-1" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.92rem' }}>
                  {showStory ? '收起故事 ↑' : '阅读真实应用场景故事 ↓'}
                </p>
                <i className={`${showStory ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-white/30 text-sm`} />
              </button>
              {showStory && (
                <div
                  className="mt-2 p-4 rounded-xl border border-white/10 bg-black/85 backdrop-blur-sm"
                  style={{ borderRadius: '12px 8px 14px 10px / 8px 14px 10px 12px' }}
                >
                  <p className="text-white/80 text-xs leading-relaxed" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>
                    {current.story}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Info panel */}
          <div className="flex-1 flex flex-col justify-center gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-14 h-14 flex items-center justify-center transition-all duration-500"
                  style={{
                    backgroundColor: `${current.tagColor}15`,
                    border: `2px solid ${current.tagColor}45`,
                    borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                  }}
                >
                  <i className={`${current.icon} text-2xl`} style={{ color: current.tagColor }} />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>{current.name}</h3>
                  <p className="text-white/40 text-xs mt-1" style={{ fontFamily: 'Caveat, cursive' }}>{current.spec}</p>
                </div>
              </div>
              <p className="text-white/55 leading-relaxed text-sm">{current.desc}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {current.features.map((f, i) => (
                <div
                  key={f}
                  className="flex items-center gap-2 p-3 rounded-xl"
                  style={{
                    background: `${current.tagColor}06`,
                    border: `1px solid ${current.tagColor}20`,
                    borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
                    opacity: mainVisible ? 1 : 0,
                    transform: mainVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `opacity 0.5s ease ${0.3 + i * 0.1}s, transform 0.5s ease ${0.3 + i * 0.1}s`,
                  }}
                >
                  <div
                    className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ backgroundColor: `${current.tagColor}22`, border: `1px solid ${current.tagColor}40` }}
                  >
                    <i className="ri-check-line text-xs" style={{ color: current.tagColor }} />
                  </div>
                  <span className="text-white/70 text-sm">{f}</span>
                </div>
              ))}
            </div>

            {/* Doodle image in info panel */}
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{ height: '120px', border: `1.5px solid ${current.tagColor}20`, borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px' }}
            >
              <img
                src={`https://readdy.ai/api/search-image?query=$%7Bcurrent.name%7D%20technology%20application%20scene%20futuristic%20neon%20glow%20dark%20background%20doodle%20art%20style%20vibrant%20colorful%20ultra%20detailed&width=600&height=120&seq=xr-panel-${current.id}-doodle-001&orientation=landscape`}
                alt={current.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${current.tagColor}20, transparent)` }} />
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 600 120" preserveAspectRatio="none" style={{ opacity: 0.3 }}>
                <path d="M0,60 Q150,30 300,60 Q450,90 600,60" stroke={current.tagColor} strokeWidth="1.5" fill="none" strokeDasharray="6,10"/>
                <circle cx="50" cy="30" r="15" stroke={current.tagColor} strokeWidth="1" fill="none" strokeDasharray="3,5"/>
                <circle cx="550" cy="90" r="18" stroke={current.tagColor} strokeWidth="1" fill="none" strokeDasharray="3,5"/>
              </svg>
            </div>

            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="flex items-center gap-2 px-6 py-3 font-bold text-sm transition-all duration-300 w-fit whitespace-nowrap hover:scale-105 cursor-pointer"
              style={{
                backgroundColor: current.tagColor,
                color: '#000',
                borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                fontFamily: 'Caveat, cursive',
                fontSize: '1rem',
                boxShadow: `0 0 20px ${current.tagColor}35`,
              }}
            >
              <span>申请体验Demo</span>
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>

        {/* Doodle image strip */}
        <div
          className="relative w-full rounded-3xl overflow-hidden mb-14"
          style={{
            height: '160px',
            opacity: mainVisible ? 1 : 0,
            transform: mainVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease 0.4s, transform 0.8s ease 0.4s',
          }}
        >
          <img
            src="https://readdy.ai/api/search-image?query=XR%20VR%20AR%20MR%20hologram%20technology%20panorama%20all%20together%20glowing%20neon%20dark%20background%20doodle%20art%20style%20vibrant%20colorful%20ultra%20wide%208k%20cinematic&width=1440&height=160&seq=xr-strip-doodle-001&orientation=landscape"
            alt="XR技术全景"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(6,14,10,0.85) 0%, rgba(6,14,10,0.2) 50%, rgba(6,14,10,0.85) 100%)' }} />
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1440 160" preserveAspectRatio="none">
            <path d="M0,80 Q360,40 720,80 Q1080,120 1440,80" stroke={current.tagColor} strokeWidth="2" fill="none" opacity="0.4" strokeDasharray="10,14"/>
            <circle cx="150" cy="40" r="22" stroke="#FFD700" strokeWidth="1.5" fill="none" opacity="0.35" strokeDasharray="4,6"/>
            <circle cx="1290" cy="120" r="28" stroke="#A855F7" strokeWidth="1.5" fill="none" opacity="0.35" strokeDasharray="4,6"/>
            <text x="50" y="100" fontSize="16" fill={current.tagColor} opacity="0.3" fontFamily="Caveat, cursive">VR · AR · MR · 全息 · 沉浸 · 未来</text>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-white/55 text-lg font-bold" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.3rem', textShadow: `0 0 20px ${current.tagColor}50` }}>
              四维感官沉浸 · 虚实边界消融
            </p>
          </div>
        </div>

        {/* Devices */}
        <div ref={devicesRef as React.RefObject<HTMLDivElement>}>
          <h3
            className="text-2xl font-bold text-white mb-3 text-center"
            style={{
              fontFamily: 'Orbitron, sans-serif',
              opacity: deviceVisible[0] ? 1 : 0,
              transform: deviceVisible[0] ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
          >
            硬件<span style={{ color: current.tagColor, transition: 'color 0.5s' }}>生态</span>
          </h3>
          <p
            className="text-center text-white/30 text-sm mb-10"
            style={{
              fontFamily: 'Caveat, cursive',
              fontSize: '1rem',
              opacity: deviceVisible[0] ? 1 : 0,
              transition: 'opacity 0.6s ease 0.1s',
            }}
          >
            META66自研硬件矩阵，打造完整的XR体验闭环
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {devices.map((d, i) => (
              <div
                key={d.name}
                className="group rounded-2xl overflow-hidden cursor-pointer"
                style={{
                  border: `1.5px solid ${d.color}20`,
                  background: `${d.color}04`,
                  borderRadius: '16px 12px 18px 14px / 12px 18px 14px 16px',
                  opacity: deviceVisible[i] ? 1 : 0,
                  transform: deviceVisible[i] ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
                  transition: `opacity 0.6s ease ${i * 120}ms, transform 0.6s cubic-bezier(0.34,1.56,0.64,1) ${i * 120}ms`,
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = `${d.color}50`; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-6px) scale(1.02)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = `${d.color}20`; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0) scale(1)'; }}
              >
                <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${d.color}, ${d.color}40)` }} />
                <div className="relative h-36 overflow-hidden">
                  <img src={d.img} alt={d.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  {/* Doodle overlay on device card */}
                  <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 300 200" preserveAspectRatio="none" style={{ opacity: 0.2 }}>
                    <path d="M0,20 Q75,5 150,22 Q225,39 300,18" stroke={d.color} strokeWidth="1.5" fill="none" strokeDasharray="5,8"/>
                    <circle cx="260" cy="170" r="18" stroke={d.color} strokeWidth="1" fill="none" strokeDasharray="3,5"/>
                  </svg>
                </div>
                <div className="p-4">
                  <p className="text-white font-bold text-sm">{d.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: d.color, fontFamily: 'Caveat, cursive', fontSize: '0.85rem' }}>{d.type}</p>
                  <p className="text-white/30 text-xs mt-1">{d.spec}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
